import { apiClient } from './client';

// Types for Call Center API responses
export interface CallCenterStats {
  total_calls_today: number;
  active_calls: number;
  waiting_calls: number;
  agents_online: number;
  agents_busy: number;
  agents_available: number;
  avg_wait_time: number;
  avg_call_duration: number;
  call_resolution_rate: number;
  customer_satisfaction: number;
}

export interface CallCenterAgent {
  id: string;
  name: string;
  email: string;
  status: 'available' | 'busy' | 'break' | 'offline';
  current_call?: string;
  calls_today: number;
  avg_call_duration: string;
  rating: number;
  department: string;
  extension: string;
  login_time?: string;
  shift_start?: string;
  shift_end?: string;
}

export interface CallCenterCall {
  id: string;
  caller_id: string;
  customer_name: string;
  customer_phone: string;
  agent_id?: string;
  agent_name?: string;
  status: 'active' | 'waiting' | 'completed' | 'failed';
  wait_time: string;
  duration?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  investment_value?: number;
  last_interaction?: string;
  notes?: string;
  started_at?: string;
  ended_at?: string;
}

export interface CallQueue {
  id: string;
  name: string;
  description: string;
  max_wait_time: number;
  current_calls: number;
  max_capacity: number;
  agents_assigned: number;
  priority_level: number;
}

export interface CallHistory {
  id: string;
  call_id: string;
  customer_name: string;
  customer_phone: string;
  agent_name: string;
  direction: 'inbound' | 'outbound';
  status: 'completed' | 'failed' | 'abandoned';
  duration: number;
  started_at: string;
  ended_at?: string;
  notes?: string;
  recording_url?: string;
  satisfaction_score?: number;
}

export interface CallMetrics {
  today: {
    total_calls: number;
    answered_calls: number;
    abandoned_calls: number;
    avg_wait_time: number;
    avg_call_duration: number;
    first_call_resolution: number;
  };
  weekly: {
    total_calls: number;
    call_volume_trend: number;
    agent_utilization: number;
    customer_satisfaction: number;
  };
  monthly: {
    total_calls: number;
    peak_hours: string[];
    top_agents: Array<{
      name: string;
      calls: number;
      rating: number;
    }>;
  };
}

export interface DashboardData {
  stats: CallCenterStats;
  agents: CallCenterAgent[];
  active_calls: CallCenterCall[];
  call_queue: CallQueue[];
  recent_calls: CallHistory[];
  metrics: CallMetrics;
}

export interface Assignment {
  id: number;
  team_id: number;
  customer_id: number;
  agent_id: number;
  is_active: boolean;
  assigned_at: string;
}

export interface SimpleCustomer {
  id: number;
  first_name?: string;
  last_name?: string;
  full_name?: string;
}

export interface CallNoteDto {
  id: number;
  team_id: number;
  customer_id: number;
  agent_id: number;
  note_text: string;
  created_at: string;
}

// Call Center API service
export const callCenterApi = {
  // Dashboard endpoints
  dashboard: {
    getStats: async (): Promise<CallCenterStats> => {
      const response = await apiClient.get('/api/v1/voip/metrics/stats');
      return response.data;
    },

    getRealTimeData: async (): Promise<DashboardData> => {
      const response = await apiClient.get('/api/v1/voip/dashboard');
      return response.data;
    },

    getMetrics: async (period: 'today' | 'weekly' | 'monthly' = 'today'): Promise<CallMetrics> => {
      const response = await apiClient.get(`/api/v1/voip/metrics/${period}`);
      return response.data;
    }
  },
  summary: {
    team: async (params: { team?: number } = {}): Promise<any> => {
      const response = await apiClient.get('/api/v1/callcenter/summary', { params });
      return response.data.data;
    },
    me: async (): Promise<any> => {
      const response = await apiClient.get('/api/v1/callcenter/summary/me');
      return response.data.data;
    }
  },

  // Agent endpoints (read-only for call center staff)
  agents: {
    list: async (): Promise<CallCenterAgent[]> => {
      const response = await apiClient.get('/api/v1/voip/agents');
      return response.data.agents || response.data;
    },

    getById: async (agentId: string): Promise<CallCenterAgent> => {
      const response = await apiClient.get(`/api/v1/voip/agents/${agentId}`);
      return response.data;
    },

    getStatus: async (agentId: string): Promise<{ status: string; current_call?: string }> => {
      const response = await apiClient.get(`/api/v1/voip/agents/${agentId}/status`);
      return response.data;
    },

    getPerformance: async (agentId: string, period: string = 'today'): Promise<any> => {
      const response = await apiClient.get(`/api/v1/voip/agents/${agentId}/performance?period=${period}`);
      return response.data;
    }
  },

  // Call management
  calls: {
    list: async (params: {
      page?: number;
      page_size?: number;
      status?: string;
      agent_id?: string;
      customer_id?: string;
      date_from?: string;
      date_to?: string;
    } = {}): Promise<{ calls: CallCenterCall[]; total: number; page: number; page_size: number }> => {
      const response = await apiClient.get('/api/v1/voip/calls', { params });
      return response.data;
    },

    getById: async (callId: string): Promise<CallCenterCall> => {
      const response = await apiClient.get(`/api/v1/voip/calls/${callId}`);
      return response.data;
    },

    getQueue: async (): Promise<CallCenterCall[]> => {
      const response = await apiClient.get('/api/v1/voip/queue-calls');
      return response.data.calls || response.data;
    },

    getActive: async (): Promise<CallCenterCall[]> => {
      const response = await apiClient.get('/api/v1/voip/active-calls');
      return response.data.calls || response.data;
    },

    getHistory: async (params: {
      page?: number;
      page_size?: number;
      agent_id?: string;
      customer_id?: string;
      date_from?: string;
      date_to?: string;
    } = {}): Promise<{ calls: CallHistory[]; total: number }> => {
      const response = await apiClient.get('/api/v1/voip/calls/history', { params });
      return response.data;
    },

    // Call actions (for staff)
    answer: async (callId: string): Promise<{ success: boolean; message?: string }> => {
      const response = await apiClient.post(`/api/v1/voip/calls/${callId}/answer`);
      return response.data;
    },

    end: async (callId: string, notes?: string): Promise<{ success: boolean; message?: string }> => {
      const response = await apiClient.post(`/api/v1/voip/calls/${callId}/end`, { notes });
      return response.data;
    },

    transfer: async (callId: string, targetAgentId: string): Promise<{ success: boolean; message?: string }> => {
      const response = await apiClient.post(`/api/v1/voip/calls/${callId}/transfer`, { 
        target_agent_id: targetAgentId 
      });
      return response.data;
    },

    addNote: async (callId: string, note: string): Promise<{ success: boolean; message?: string }> => {
      const response = await apiClient.post(`/api/v1/voip/calls/${callId}/notes`, { note });
      return response.data;
    }
  },

  // Queue management
  queues: {
    list: async (): Promise<CallQueue[]> => {
      const response = await apiClient.get('/api/v1/voip/queues');
      return response.data.queues || response.data;
    },

    getById: async (queueId: string): Promise<CallQueue> => {
      const response = await apiClient.get(`/api/v1/voip/queues/${queueId}`);
      return response.data;
    },

    getStats: async (queueId: string): Promise<any> => {
      const response = await apiClient.get(`/api/v1/voip/queues/${queueId}/stats`);
      return response.data;
    }
  },

  // Customer data (with restrictions for call center staff)
  customers: {
    search: async (query: string): Promise<any[]> => {
      const response = await apiClient.get('/api/v1/customers/search', {
        params: { q: query, role: 'staff' } // Staff role for data restrictions
      });
      return response.data.customers || response.data;
    },

    getById: async (customerId: string): Promise<any> => {
      const response = await apiClient.get(`/api/v1/customers/${customerId}`, {
        params: { role: 'staff' } // Staff role for data restrictions
      });
      return response.data;
    },

    getCallHistory: async (customerId: string, limit: number = 10): Promise<CallHistory[]> => {
      const response = await apiClient.get(`/api/v1/customers/${customerId}/calls`, {
        params: { limit, role: 'staff' }
      });
      return response.data.calls || response.data;
    }
  },

  // Real-time updates (WebSocket will be implemented later)
  realtime: {
    subscribe: (callback: (data: any) => void) => {
      // TODO: Implement WebSocket connection
      console.log('Real-time subscription started');
      return () => console.log('Real-time subscription ended');
    }
  },

  assignments: {
    create: async (customerId: number, agentId: number): Promise<Assignment> => {
      const response = await apiClient.post('/api/v1/callcenter/assign', null, { params: { customer_id: customerId, agent_id: agentId } });
      return response.data.data;
    },
    list: async (params: { agent_id?: number; customer_id?: number; is_active?: boolean } = {}): Promise<Assignment[]> => {
      const response = await apiClient.get('/api/v1/callcenter/assignments', { params });
      return response.data.data || [];
    },
    myCustomers: async (): Promise<SimpleCustomer[]> => {
      const response = await apiClient.get('/api/v1/callcenter/my/customers');
      return response.data.data || [];
    },
    unassign: async (customerId: number): Promise<{ updated: number }> => {
      const response = await apiClient.post('/api/v1/callcenter/unassign', null, { params: { customer_id: customerId } });
      return { updated: response.data?.updated || 0 };
    }
  },

  notes: {
    create: async (customerId: number, noteText: string): Promise<CallNoteDto> => {
      const response = await apiClient.post(`/api/v1/callcenter/customers/${customerId}/notes`, null, { params: { note_text: noteText } });
      return response.data.data;
    },
    list: async (customerId: number): Promise<CallNoteDto[]> => {
      const response = await apiClient.get(`/api/v1/callcenter/customers/${customerId}/notes`);
      return response.data.data || [];
    },
    deleteAdmin: async (noteId: number): Promise<void> => {
      await apiClient.delete(`/api/v1/callcenter/notes/${noteId}`);
    }
  },

  // Team investments
  investments: {
    list: async (params: {
      page?: number;
      page_size?: number;
      status?: string;
      bank_id?: number;
      customer_id?: number;
      date_from?: string;
      date_to?: string;
      min_amount?: number;
      max_amount?: number;
      search?: string;
      team?: number;
    } = {}): Promise<{ items: any[]; total: number; page: number; page_size: number; has_next: boolean; has_prev: boolean }> => {
      const response = await apiClient.get('/api/v1/callcenter/investments', { params });
      return response.data.data;
    },
    my: async (params: { page?: number; page_size?: number; status?: string; bank_id?: number; search?: string } = {}): Promise<{ items: any[]; total: number; page: number; page_size: number; has_next: boolean; has_prev: boolean }> => {
      const response = await apiClient.get('/api/v1/callcenter/investments/me', { params });
      return response.data.data;
    }
  }
};

// Utility functions for formatting
export const formatCallDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

export const formatWaitTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'available': return 'text-green-600 bg-green-50 border-green-200';
    case 'busy': return 'text-red-600 bg-red-50 border-red-200';
    case 'break': return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'offline': return 'text-gray-600 bg-gray-50 border-gray-200';
    case 'active': return 'text-green-600 bg-green-50 border-green-200';
    case 'waiting': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'completed': return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'failed': return 'text-red-600 bg-red-50 border-red-200';
    default: return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

export const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
    case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
    case 'normal': return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
    default: return 'text-gray-600 bg-gray-50 border-gray-200';
  }
}; 